import { motion } from 'framer-motion';
import { getCategories } from '@/utils/categories';
import { Card, CardContent } from "@/components/ui/card";
import { Filter } from 'lucide-react';
import CategoryProposalModal from "@/components/categories/CategoryProposalModal";

const CategoriesShowcase = () => {
  const categories = getCategories();

  const container = {
    hidden: { opacity: 0 },
    visible: { opacity: 1, transition: { staggerChildren: 0.06 } }
  };
  const item = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0 }
  };

  return (
    <div>
      <div className="max-w-3xl mb-12 md:mb-16">
        <span className="ed-eyebrow">01 — Catégories</span>
        <h2 className="ed-heading text-3xl md:text-5xl mt-5 mb-4">
          Tout ce que vous pouvez <span className="accent">signaler</span>
        </h2>
        <p className="ed-subtle text-base md:text-lg max-w-xl">
          Une grille éditoriale couvrant les principaux désordres urbains. Chaque catégorie est traitée avec la même rigueur de suivi.
        </p>
      </div>

      <div className="flex items-center gap-3 mb-8 md:mb-10">
        <button className="ed-btn-ghost flex items-center gap-2 text-sm py-2 px-4">
          <Filter className="w-4 h-4" /> Filtrer
        </button>
        <CategoryProposalModal />
      </div>

      {/* Mobile horizontal scroll */}
      <div className="md:hidden">
        <motion.div
          className="flex gap-3 overflow-x-auto pb-4 snap-x snap-mandatory scrollbar-hide -mx-4 px-4"
          variants={container} initial="hidden" animate="visible"
        >
          {categories.map((category) => (
            <motion.div key={category.id} variants={item} className="snap-start shrink-0 w-[260px]">
              <Card className="ed-card overflow-hidden h-full rounded-sm">
                <CardContent className="p-0">
                  {category.imageUrl && (
                    <div className="relative h-32 overflow-hidden">
                      <img src={category.imageUrl} alt={category.label} className="w-full h-full object-cover" loading="lazy" />
                      <div className="absolute inset-0 bg-gradient-to-t from-[#0f1b3d]/80 to-transparent" />
                      <div className="absolute bottom-0 left-0 p-3 flex items-center gap-2 text-white">
                        <category.icon className="w-3.5 h-3.5" />
                        <span className="font-display text-[11px] uppercase tracking-[0.18em]">{category.label}</span>
                      </div>
                    </div>
                  )}
                  <div className="p-4">
                    <p className="text-xs text-[#0a0f1f]/65 leading-relaxed line-clamp-3">{category.description}</p>
                  </div>
                </CardContent>
              </Card>
            </motion.div>
          ))}
        </motion.div>
      </div>

      {/* Asymmetric editorial grid on desktop */}
      <motion.div
        className="hidden md:grid md:grid-cols-6 gap-6"
        variants={container} initial="hidden" whileInView="visible" viewport={{ once: true }}
      >
        {categories.map((category, idx) => {
          // Editorial rhythm: alternate spans for staggered feel
          const span = idx % 5 === 0 ? "md:col-span-3" : idx % 5 === 1 ? "md:col-span-3" : "md:col-span-2";
          return (
            <motion.div key={category.id} variants={item} className={span}>
              <Card className="ed-card overflow-hidden h-full rounded-sm">
                <CardContent className="p-0">
                  {category.imageUrl && (
                    <div className="relative h-44 overflow-hidden">
                      <img
                        src={category.imageUrl}
                        alt={category.label}
                        className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-[#0f1b3d]/85 via-[#0f1b3d]/20 to-transparent" />
                      <div className="absolute bottom-0 left-0 p-4 flex items-center gap-2 text-white">
                        <category.icon className="w-4 h-4" />
                        <span className="font-display text-xs uppercase tracking-[0.22em]">{category.label}</span>
                      </div>
                    </div>
                  )}
                  <div className="p-5">
                    <p className="text-sm text-[#0a0f1f]/70 leading-relaxed">{category.description}</p>
                  </div>
                </CardContent>
              </Card>
            </motion.div>
          );
        })}
      </motion.div>
    </div>
  );
};

export default CategoriesShowcase;
